import React, { useContext, useState } from 'react'
import {
  DcButton,
  DcDropDown,
  DcInput,
  PixelFactoryContext,
  PixelPageContainer,
  PixelSpinner,
  SectionContent,
} from 'pixel-base'
import styled from 'styled-components'
import { createJournal } from 'utils/api-routes-accounts'
import SaveIcon from 'utils/icons/save-icon'
import XmarkIcon from 'utils/icons/xmark-icon'

const Content = styled.div`
  padding: 20px;
`

const FormGroup = styled.div`
  margin-bottom: 20px;
`

const Footer = styled.div`
  padding: 20px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

interface PixelFactory {
  showSuccess?: (msg: string) => void
  showFailure?: (msg: string) => void
  showPageLoader?: () => void
  hidePageLoader?: () => void
}

interface AddNewJournalProps {
  onClose: () => void
  onSuccess: (_response: any, _action: string) => void
}

const AddNewJournal: React.FC<AddNewJournalProps> = ({
  onClose,
  onSuccess,
}) => {
  const pixelFactory = useContext(PixelFactoryContext) as PixelFactory
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    journal_type: 'general',
    currency_code: 'PKR',
    sequence_prefix: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSubmit = () => {
    setLoading(true)
    setError(null)

    createJournal(formData).subscribe({
      next: (response) => {
        pixelFactory?.showSuccess?.('Journal created successfully')
        onSuccess(response, 'add')
        setLoading(false)
      },
      error: () => {
        setError('Failed to create journal. Please try again.')
        setLoading(false)
      },
    })
  }

  return (
    <PixelPageContainer>
      <SectionContent>
        <Content>
          <FormGroup>
            <DcInput
              label="Name"
              value={formData.name}
              placeholder="Enter Journal Name"
              onChange={(e) => handleChange('name', e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <DcInput
              label="Code"
              value={formData.code}
              placeholder="Enter Journal Code"
              onChange={(e) => handleChange('code', e.target.value)}
              required
            />
          </FormGroup>
          <FormGroup>
            <DcDropDown
              label="Type"
              value={formData.journal_type}
              placeholder="Select Journal Type"
              onChange={(e) => handleChange('journal_type', e.target.value)}
              options={[
                { value: 'general', label: 'General' },
                { value: 'sale', label: 'Sale' },
                { value: 'purchase', label: 'Purchase' },
                { value: 'cash', label: 'Cash' },
                { value: 'bank', label: 'Bank' },
                { value: 'adjustment', label: 'Adjustment' },
              ]}
              required
            />
          </FormGroup>
          <FormGroup>
            <DcInput
              label="Sequence Prefix"
              value={formData.sequence_prefix}
              placeholder="Optional prefix"
              onChange={(e) => handleChange('sequence_prefix', e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <DcInput
              label="Currency"
              value={formData.currency_code}
              placeholder="PKR"
              onChange={(e) => handleChange('currency_code', e.target.value)}
            />
          </FormGroup>
          {error && (
            <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>
          )}
        </Content>
        <Footer>
          <DcButton variant="outline-secondary" onClick={onClose} size="sm">
            <XmarkIcon size={16} /> <span>Cancel</span>
          </DcButton>
          <DcButton
            data-testid="submit-button"
            variant="primary"
            onClick={handleSubmit}
            disabled={loading}
            size="sm"
          >
            {loading ? (
              <PixelSpinner size="sm" data-testid="spinner" />
            ) : (
              <SaveIcon size={16} />
            )}
            <span>Save</span>
          </DcButton>
        </Footer>
      </SectionContent>
    </PixelPageContainer>
  )
}

export default AddNewJournal
